import { useMemo, useCallback } from 'react';
import { ControlProps, ControlElement, Resolve, JsonSchema } from '@jsonforms/core';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useValidationContext } from '../../RpcForm';
import './AutocompleteControl.styles.scss';

export const AutocompleteControl = withJsonFormsControlProps((props: ControlProps) => {
  const { data, handleChange, path, label, required, errors, schema, uischema } = props;
  const ctx = useJsonForms();


  // Access validation state from our custom context (more reliable than JSON Forms context)
  let shouldShowErrors = false;
  try {
    const context = useValidationContext();
    shouldShowErrors = context.showErrors;
  } catch (e) {
    // Context might not be available, fall back to JSON Forms context
    // Silently fall back - this is expected in some cases
  }

  const jsonFormsValidationMode = ctx?.core?.validationMode || 'ValidateAndHide';
  const jsonFormsShowErrors = jsonFormsValidationMode === 'ValidateAndShow';

  // Use our context if available, otherwise fall back to JSON Forms context
  const showErrors = shouldShowErrors || jsonFormsShowErrors;


  // Get validation errors for this specific path
  const pathErrors = useMemo(() => {
    if (!ctx?.core) return [];

    // Get all validation errors from the core state
    const allErrors = ctx.core.errors || [];

    // Filter errors that match this field's path
    return allErrors.filter((error: any) => {
      const errorPath = error.instancePath || error.schemaPath || '';
      // Normalize paths for comparison
      // JSON Forms uses #/properties/exchange/properties/platform format
      // Our path might be #/exchange/platform or exchange/platform
      const normalizedErrorPath = errorPath.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
      const normalizedFieldPath = path.replace(/^#\//, '').replace(/\//g, '.');

      // Only match errors for this field (avoid showing signal_parameters errors on Signal)
      return normalizedErrorPath === normalizedFieldPath ||
        normalizedErrorPath.endsWith(`.${normalizedFieldPath}`);
    });
  }, [ctx?.core?.errors, path]);

  // Combine props errors with context errors
  // Also check if field is required and empty when validation should be shown
  const allErrors = useMemo(() => {
    const combined = [...errors];
    pathErrors.forEach((error: any) => {
      const errorMsg = error.message || error;
      if (errorMsg && typeof errorMsg === 'string' && !combined.includes(errorMsg)) {
        combined.push(errorMsg);
      }
    });

    // If validation should be shown and field is required but empty, add error
    // This handles cases where JSON Forms doesn't report errors for nested required fields
    const isEmpty = data === undefined || data === null || data === '';
    if (showErrors && required && isEmpty) {
      if (!combined.includes('This field is required')) {
        combined.push('This field is required');
      }
    }


    return combined;
  }, [errors, pathErrors, showErrors, required, data, path, jsonFormsValidationMode]);

  // Resolve schema from root so enums inside allOf/$ref (hedge_type, platform, router) are visible
  const resolvedSchema = useMemo(() => {
    const control = uischema as ControlElement;
    const scope = control?.scope;
    const root = ctx?.core?.schema as JsonSchema | undefined;
    if (!root || !scope) return schema;
    try {
      return Resolve.schema(root, scope, root) as JsonSchema;
    } catch {
      return schema;
    }
  }, [schema, uischema, ctx?.core?.schema]);

  // Get enum options, following allOf/$ref (e.g. hedge_type: allOf [ { $ref: "#/definitions/HedgeType" } ])
  const options = useMemo(() => {
    const root = ctx?.core?.schema as JsonSchema | undefined;
    const getEnum = (s: JsonSchema | undefined): unknown[] | undefined => {
      if (!s || typeof s !== 'object') return undefined;
      const e = (s as { enum?: unknown[] }).enum;
      if (Array.isArray(e)) return e;
      // Top-level $ref (e.g. nested platform/router: { $ref: "#/definitions/Platform" })
      const ref = (s as { $ref?: string }).$ref;
      if (ref && root) {
        try {
          const resolved = Resolve.schema(root, ref, root) as JsonSchema;
          const fromRef = getEnum(resolved);
          if (Array.isArray(fromRef)) return fromRef;
        } catch {
          /* ignore */
        }
      }
      const allOf = (s as { allOf?: { $ref?: string; enum?: unknown[] }[] }).allOf;
      if (Array.isArray(allOf) && root) {
        for (const item of allOf) {
          if (item?.$ref) {
            try {
              const resolved = Resolve.schema(root, item.$ref, root) as JsonSchema;
              const fromRef = getEnum(resolved);
              if (Array.isArray(fromRef)) return fromRef;
            } catch {
              /* ignore */
            }
          } else if (item?.enum && Array.isArray(item.enum)) {
            return item.enum;
          }
        }
      }
      return undefined;
    };
    return getEnum(resolvedSchema as JsonSchema) ?? (schema as { enum?: unknown[] })?.enum ?? [];
  }, [resolvedSchema, schema?.enum, ctx?.core?.schema, uischema]);

  const handleSelectChange = useCallback(
    (e: { target: { value: unknown } }) => {
      const v = e.target.value;
      if (v === '' || v === undefined || v === null) {
        const effectiveSchema = (resolvedSchema as JsonSchema) ?? schema;
        const defaultValue = effectiveSchema?.type === 'string' ? '' : undefined;
        handleChange(path, defaultValue);
      } else {
        handleChange(path, v);
      }
    },
    [handleChange, path, resolvedSchema, schema]
  );

  const hasError = allErrors.length > 0;
  const errorMessage = allErrors.length > 0 ? allErrors.join(', ') : '';

  // Select value: must be one of the option values (string/number); empty for "none"
  const selectValue = data === undefined || data === null ? '' : data;

  return (
    <div className="rpc-autocomplete-control">
      <FormControl fullWidth variant="outlined" error={hasError} required={required}>
        <InputLabel id={`${path}-label`}>{label}</InputLabel>
        <Select
          labelId={`${path}-label`}
          value={selectValue}
          onChange={handleSelectChange}
          label={label}
          displayEmpty
          renderValue={(v) => (v === '' || v === undefined || v === null ? '' : String(v))}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((opt, idx) => (
            <MenuItem key={idx} value={opt as string | number}>
              {String(opt)}
            </MenuItem>
          ))}
        </Select>
        {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
      </FormControl>
    </div>
  );
});
